"use client";

import { createContext, useContext, useEffect, useRef } from "react";
import { Inbox, Loader2, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { useResizablePane } from "@/lib/use-resizable-pane";

/**
 * Canonical split-view shell for queue-like operator surfaces.
 *
 * Issues, Inbox, Patches, Follow-ups (and any future queue) compose
 * this same primitive so resizing, sticky positioning, scroll model,
 * keyboard navigation, row hover/selected/active states, and the
 * empty/loading detail-pane stubs are mechanically identical across
 * the operator product.
 *
 * Two layout variants share the same resize hook (`useResizablePane`),
 * the same drag-handle gesture model, the same persistence shape,
 * the same mobile-collapse breakpoint, and the same aside chrome:
 *
 *   variant="detail-aside" (default) — Linear/Jira shape
 *     [ list (flex-1)   |  handle  |  detail (fixed width)   ]
 *     The list IS the focus; the detail surface is an auxiliary
 *     right pane. Used by Issues; Patches and Follow-ups adopt
 *     it next.
 *
 *   variant="list-aside" — Gmail/Inbox shape
 *     [ list (fixed)    |  handle  |  detail (flex-1)        ]
 *     The detail surface IS the working area; the list is a
 *     scan column. Used by Inbox.
 *
 * The drag handle paints identically in both variants; only the
 * direction the gesture grows differs (mirrored math).
 *
 * SplitView itself owns no styling for the row content — pages keep
 * their existing rows (DataTable rows for Issues/Patches; bespoke
 * email rows for Inbox). The shell standardizes layout, scroll,
 * focus chrome, sticky behavior, and the empty/loading slots.
 */

export type SplitViewVariant = "detail-aside" | "list-aside";

type SplitViewContextValue = {
  pane: ReturnType<typeof useResizablePane>;
  variant: SplitViewVariant;
};

const SplitViewContext = createContext<SplitViewContextValue | null>(null);

function useSplitViewContext(component: string): SplitViewContextValue {
  const ctx = useContext(SplitViewContext);
  if (!ctx) {
    throw new Error(`<${component}> must be rendered inside <SplitView>`);
  }
  return ctx;
}

export type SplitViewProps = {
  /**
   * Pane state instance. The owning page calls `useResizablePane`
   * with its own storageKey + width defaults; SplitView only reads
   * the state.
   */
  pane: ReturnType<typeof useResizablePane>;
  /** Layout orientation. Defaults to `detail-aside`. */
  variant?: SplitViewVariant;
  children: React.ReactNode;
  className?: string;
};

export function SplitView({ pane, variant = "detail-aside", children, className }: SplitViewProps) {
  return (
    <SplitViewContext.Provider value={{ pane, variant }}>
      <div className={cn("flex items-stretch gap-0", className)}>
        {children}
      </div>
    </SplitViewContext.Provider>
  );
}

/* ── List slot ────────────────────────────────────────────────────
 * For `detail-aside`, the list is `flex-1` (fills remaining width).
 * For `list-aside`, the list is fixed-width per `pane.width`. When
 * the pane is collapsed (mobile or toggled off), the list expands
 * back to full width — operators always see the queue.
 */
function List({ children, className }: { children: React.ReactNode; className?: string }) {
  const { pane, variant } = useSplitViewContext("SplitView.List");

  if (variant === "list-aside") {
    if (!pane.enabled) {
      // Pane collapsed → list takes the entire row.
      return <div className={cn("flex-1 min-w-0", className)}>{children}</div>;
    }
    return (
      <div
        style={{ width: `${pane.width}px` }}
        className={cn("shrink-0 self-stretch min-w-0 flex flex-col", className)}
      >
        {children}
      </div>
    );
  }

  // detail-aside: list grows.
  return <div className={cn("flex-1 min-w-0", className)}>{children}</div>;
}

/* ── Resize handle ────────────────────────────────────────────────
 * Paints identically in both variants. Mouse drag mutates the
 * correct sibling's inline width per variant; keyboard nudges
 * commit through `pane.commit`.
 *
 *   detail-aside → handle's NEXT sibling is the detail aside;
 *                  drag right → window.innerWidth - clientX.
 *   list-aside   → handle's PREVIOUS sibling is the list;
 *                  drag right → clientX - container.left.
 */
function ResizeHandle({ ariaLabel }: { ariaLabel: string }) {
  const { pane, variant } = useSplitViewContext("SplitView.ResizeHandle");
  const draggingRef = useRef(false);
  const liveWidthRef = useRef(pane.width);
  const handleRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { liveWidthRef.current = pane.width; }, [pane.width]);

  if (!pane.enabled) return null;

  const startDrag = () => {
    draggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const target = variant === "detail-aside"
      ? handleRef.current?.nextElementSibling as HTMLElement | null
      : handleRef.current?.previousElementSibling as HTMLElement | null;
    const containerRect = handleRef.current?.parentElement?.getBoundingClientRect();

    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      const raw = variant === "detail-aside"
        ? window.innerWidth - e.clientX
        : containerRect ? e.clientX - containerRect.left : e.clientX;
      const next = Math.min(pane.max, Math.max(pane.min, raw));
      liveWidthRef.current = next;
      if (target) target.style.width = `${next}px`;
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      pane.commit(liveWidthRef.current);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    // Mirror the gesture: left arrow grows the controlled pane in
    // detail-aside (because the controlled pane is on the right),
    // shrinks it in list-aside. Same arrow → same screen direction.
    const step = e.shiftKey ? 64 : 16;
    const grow = variant === "detail-aside"
      ? e.key === "ArrowLeft"
      : e.key === "ArrowRight";
    const shrink = variant === "detail-aside"
      ? e.key === "ArrowRight"
      : e.key === "ArrowLeft";
    if (grow) {
      pane.commit(pane.width + step);
      e.preventDefault();
    } else if (shrink) {
      pane.commit(pane.width - step);
      e.preventDefault();
    } else if (e.key === "Home") {
      pane.commit(pane.max);
      e.preventDefault();
    } else if (e.key === "End") {
      pane.commit(pane.min);
      e.preventDefault();
    }
  };

  return (
    <button
      ref={handleRef}
      type="button"
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={pane.width}
      aria-valuemin={pane.min}
      aria-valuemax={pane.max}
      onMouseDown={startDrag}
      onKeyDown={onKeyDown}
      className="hidden md:block w-1 shrink-0 bg-line-subtle hover:bg-brand-500/40 active:bg-brand-500/60 cursor-col-resize touch-none focus-ring focus:bg-brand-500/40"
    />
  );
}

/* ── Detail pane ──────────────────────────────────────────────────
 * Sticky-top, full viewport height, identical chrome in both
 * variants. Internally wraps a scroll surface so the detail content
 * scrolls independently of the list. Renders nothing when the pane
 * is collapsed (mobile or toggled off).
 *
 * For `list-aside`, the detail is `flex-1` (no fixed width); the
 * list owns the resizable width. For `detail-aside`, the detail
 * itself takes the resizable width.
 */
function Detail({
  children,
  className,
  ariaLabel,
  resizeAriaLabel,
  innerClassName,
}: {
  children: React.ReactNode;
  className?: string;
  /** Aria-label for the surrounding `<aside>`. */
  ariaLabel: string;
  /** Aria-label for the resize handle. */
  resizeAriaLabel: string;
  /**
   * Override the scroll container's classes. Defaults to
   * `flex-1 overflow-y-auto subtle-scroll` so the detail content
   * always has its own scrollbar without affecting the list.
   */
  innerClassName?: string;
}) {
  const { pane, variant } = useSplitViewContext("SplitView.Detail");
  if (!pane.enabled) return null;

  const variantWidth = variant === "detail-aside"
    ? { width: `${pane.width}px` }
    : undefined;

  return (
    <>
      <ResizeHandle ariaLabel={resizeAriaLabel} />
      <aside
        role="complementary"
        aria-label={ariaLabel}
        style={variantWidth}
        className={cn(
          "hidden md:flex flex-col self-stretch border-l border-line-subtle bg-bg-raised/30 sticky top-0 h-[calc(100vh-1rem)]",
          variant === "detail-aside" ? "shrink-0" : "flex-1 min-w-0",
          className,
        )}
      >
        <div className={cn(innerClassName ?? "flex-1 overflow-y-auto subtle-scroll min-w-0")}>
          {children}
        </div>
      </aside>
    </>
  );
}

/* ── Empty / Loading slots ────────────────────────────────────────
 * Canonical pane states. Operator-density: a single small icon, one
 * line of copy, no illustration. Replace bespoke "select an item to
 * view its detail" cards each page used to render.
 */
function Empty({
  icon: Icon = Inbox,
  title,
  hint,
  className,
}: {
  icon?: LucideIcon;
  title?: React.ReactNode;
  hint?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={cn(
        "h-full grid place-items-center text-center text-fg-muted p-8",
        className,
      )}
    >
      <div className="space-y-2 max-w-[36ch]">
        <Icon size={22} className="mx-auto text-fg-muted/60" aria-hidden />
        {title && <div className="text-[12.5px] text-fg-dim">{title}</div>}
        {hint && <p className="text-[11.5px] text-fg-muted">{hint}</p>}
      </div>
    </div>
  );
}

function Loading({ label, className }: { label?: React.ReactNode; className?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "h-full grid place-items-center text-[12px] text-fg-muted",
        className,
      )}
    >
      <div className="inline-flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" aria-hidden />
        <span>{label ?? "Loading…"}</span>
      </div>
    </div>
  );
}

SplitView.List = List;
SplitView.Detail = Detail;
SplitView.Empty = Empty;
SplitView.Loading = Loading;
